import 'nsw-design-system/src/components/card/_card.scss';
import './card-container.css';
import {
  ul as ulElem,
  li as liElem,
  div as divElem,
  span as spanElem,
  moveChildrenTo,
} from '../../scripts/dom-helpers.js';

import { createOptimizedPicture } from '../../scripts/aem.js';

const CardClasses = {
  highlight: 'nsw-card--highlight',
  dark: 'nsw-card--dark',
  light: 'nsw-card--light',
  horizontal: 'nsw-card--horizontal',
};

/**
 * Enhances the image column in a card:
 * - Moves <picture> out of a wrapping <p>, if present.
 * - Adds the correct class to the image column.
 * - Removes 'horizontal' class from block if no image is present.
 *
 * @param {HTMLElement} imageColumn - Column possibly containing an image.
 * @param {HTMLElement} block - Card block element.
 */
function decorateImageColumn(imageColumn, block) {
  // Make the first column the image container if there is an image
  if (imageColumn?.querySelector('picture')) {
    imageColumn.className = 'nsw-card__image';
    // Temp fix: Remove after manual update of "About Us" card-container in authoring
    if (imageColumn.children.length === 1 && imageColumn.children[0] !== imageColumn.querySelector('picture')) {
      const pictureEl = imageColumn.querySelector('picture');
      imageColumn.children[0]?.remove();
      imageColumn.insertBefore(pictureEl, imageColumn.firstChild);
    }
  } else {
    block.classList.remove('horizontal');
  }
}

function decorateHeadlineColumn(headlineColumn, cardContent) {
  // the Second column always has a headline

  const titleWrapper = divElem({ class: 'nsw-card__title' });

  // move the children of the second column to the titleWrapper
  moveChildrenTo(headlineColumn, titleWrapper);

  // remove the button wrapper around the <a> tag
  const a = titleWrapper.querySelector('a');
  const p = a?.closest('p');
  if (p) {
    p.replaceWith(a);
    a.classList.remove('button');
  }
  cardContent.append(titleWrapper);
}

function decorateTextColumn(textColumn, cardContent) {
  // ===== Column (3) - Copy text (optional) =====
  if (textColumn.innerHTML.trim()) {
    textColumn.classList.add('nsw-card__copy');

    // insert inside the third div
    cardContent.append(textColumn);
  } else {
    // remove the third column if empty
    textColumn.remove();
  }
}

function addArrowIcon(cardContent) {
  // Add the arrow icon to indicate that the card is clickable
  const span = spanElem({
    class: 'material-icons nsw-material-icons',
    'aria-hidden': 'true',
    focusable: 'false',
  }, 'east');

  cardContent.append(span);
}

function addCardVariationClasses(block, cardContainer) {
  // add variation classes
  Object.keys(CardClasses).forEach((key) => {
    if (block.classList.contains(key)) {
      cardContainer.classList.add(CardClasses[key]);
    }
  });

  if (!cardContainer?.querySelector('.nsw-card__copy')) {
    cardContainer.classList.add('nsw-card--headline');
  }
}

export default function decorate(block) {
  /* change to ul, li */
  const ul = ulElem({ class: 'nsw-grid' });

  [...block.children].forEach((card) => {
    const cardContainer = divElem({ class: 'nsw-card' });
    moveChildrenTo(card, cardContainer);

    let cardHasMultipleLinks = false;
    const numberOfLinks = cardContainer.querySelectorAll('a').length;
    if (numberOfLinks > 1) {
      cardHasMultipleLinks = true;
    }

    // check if the page is page-template
    const cardLayout = document.querySelector('.page-template') ? 'nsw-col nsw-col-md-6 nsw-col-lg-6' : 'nsw-col nsw-col-md-6 nsw-col-lg-4';

    const gridItem = liElem({ class: cardLayout }, cardContainer);

    const [imageColumn, textColumn] = cardContainer.children;

    decorateImageColumn(imageColumn, block);

    const cardContent = divElem({ class: 'nsw-card__content' });

    const headlineColumn = textColumn.firstElementChild;
    if (headlineColumn?.querySelector('a')) {
      decorateHeadlineColumn(headlineColumn, cardContent);
      textColumn.removeChild(headlineColumn);
    }

    decorateTextColumn(textColumn, cardContent);

    addArrowIcon(cardContent);

    cardContainer.append(cardContent);

    addCardVariationClasses(block, cardContainer);

    if (cardHasMultipleLinks) {
      // if there are multiple links, add a class to the card
      gridItem.classList.add('nsw-card--multiple-links');
    }

    ul.append(gridItem);
  });

  // SWS-1515 - focal point implementation
  ul.querySelectorAll('picture > img').forEach((img) => {
    const optimizedPic = createOptimizedPicture(img.src, img.alt, false, [{ width: '750' }]);
    const { title } = img.dataset;
    img.closest('picture')?.replaceWith(optimizedPic);

    let [x, y] = [50, 50];
    if (title?.includes('data-focal')) {
      [x, y] = title.split(':')[1].split(',');
    }
    const optimizedImg = optimizedPic.querySelector('img');
    optimizedImg.style.objectPosition = `${x}% ${y}%`;
  });

  block.textContent = '';
  block.append(ul);
}
